import { timestampDate } from "@bufbuild/protobuf/wkt";
import type { Poll } from "#gen/forms/v1/poll_pb.js";

export enum PollState {
	PreStart = 0,
	Pending = 1,
	Active = 2,
	Closed = 3,
	PreResults = 4,
	PostResults = 5,
}

const states: Record<string, (poll: Poll) => PollState | undefined> = {
	[PollState.PreStart]: (poll: Poll) => {
		if (isPollPreStart(poll)) {
			return undefined;
		}
		if (isPollPending(poll)) {
			return PollState.Pending;
		}
		return PollState.Active;
	},
	[PollState.Pending]: (poll: Poll) => {
		if (isPollPending(poll)) {
			return undefined;
		}
		return PollState.Active;
	},
	[PollState.Active]: (poll: Poll) => {
		if (isPollClosed(poll)) {
			if (isPreResults(poll)) {
				return PollState.PreResults;
			}
			return PollState.Closed;
		}
		return undefined;
	},
	[PollState.Closed]: (poll: Poll) => {
		if (isPostResults(poll)) {
			return PollState.PostResults;
		}
		return undefined;
	},
	[PollState.PreResults]: (poll: Poll) => {
		if (isPreResults(poll)) {
			return undefined;
		}
		return PollState.Closed;
	},
	[PollState.PostResults]: (_poll: Poll) => {
		return undefined;
	},
};

function isPollPreStart(poll: Poll): boolean {
	if (!poll.preVoteStart) {
		return false;
	}
	return timestampDate(poll.preVoteStart).getTime() > Date.now();
}

function isPollPending(poll: Poll): boolean {
	if (!poll.window?.start) {
		return false;
	}
	return timestampDate(poll.window.start).getTime() > Date.now();
}

function isPollClosed(poll: Poll): boolean {
	if (!poll.window?.end) {
		return false;
	}
	return timestampDate(poll.window.end).getTime() < Date.now();
}

function isPreResults(poll: Poll): boolean {
	if (!poll.postVoteResultsWindow?.start) {
		return false;
	}
	return timestampDate(poll.postVoteResultsWindow.start).getTime() > Date.now();
}

function isPostResults(poll: Poll): boolean {
	if (!poll.postVoteResultsWindow?.end) {
		return false;
	}
	return timestampDate(poll.postVoteResultsWindow.end).getTime() < Date.now();
}

export function getNextState(poll: Poll, currentState: PollState): PollState | undefined {
	return states[currentState](poll);
}

export function getPollState(poll: Poll): PollState {
	let result: PollState = PollState.PreStart;
	let nextState: PollState | undefined;
	do {
		nextState = states[result](poll);
		if (nextState !== undefined) {
			result = nextState;
		}
	} while (nextState !== undefined);
	return result;
}
